import { combine, createEffect, createEvent, createStore, sample } from 'effector';
import type { EventBusRequest, SceneOpenEvent } from '../shared/eventBus';
import type { TourItem } from '../shared/assistantMessage';
import type { HistoricalSceneSlide } from '../scene/sceneConfig';
import { publishEventBusEventSync } from '../services/assistantClient';
import { $assistantSessionId, assistantStreamSessionReceived } from './assistantSession';
import { $locale } from './locale';
import { buildEventBusContext, isSceneOpenSupported } from './sceneOpen';
import { $sceneProgressByPoiFilterId, tourReceived, type SceneProgress } from './visitedSights';
import { SCENE_POI_ID } from '../scene/sceneNavigationMap';
import {
  getCanonicalInsideSceneId,
  getInsideScenesForParent,
  isInsideScene,
} from '../scene/insideScenes/registry';
import { resolveHistoricalFamily } from './historicalScenes';
import { $activeSceneId } from './sceneNavigation';

/**
 * Menu progress prefetch.
 *
 * The tabbed menu shows per-scene "{visited} of {total} spots" counters
 * (and per-tab POI aggregates) for ALL scenes — including ones the
 * user hasn't navigated to yet. `visited`/`total` come from the backend
 * `tour` (`$tourByScene` in `visitedSights.ts`), which is normally only
 * populated for scenes opened this session via the `scene_open` flow.
 *
 * To fill the gaps WITHOUT navigating, `menuOpened` fires a lightweight
 * `scene_open` POST to `/api/stream/sync` for every menu scene that has no
 * cached tour yet, and routes ONLY the `tour` field into `tourReceived`
 * (tagged `origin: 'prefetch'` so analytics milestone trackers ignore it —
 * the user never entered these scenes). It deliberately does NOT touch the
 * chat (`assistantStreamMessageReceived`), the intro buffer,
 * `$pendingSceneOpenRequests`, or the chat-input gate — unlike the
 * navigation-driven flow in `sceneOpen.ts`. After the first open, the cache
 * is warm and the menu simply reads `$tourByScene`.
 *
 * (Per product decision: "use /api/stream/sync the first time, then rely on
 * the tour cache".)
 */

/** Основные сцены меню в порядке отображения (URL-id). `overall` — не карточка. */
export const MENU_MAIN_SCENE_URL_IDS: readonly string[] = [
  'kaaba',
  'mina',
  'muzdalifah',
  'arafat',
];

const MAIN_POI_FILTER_IDS: readonly string[] = MENU_MAIN_SCENE_URL_IDS.map(
  (urlId) => SCENE_POI_ID[urlId] ?? urlId,
);

/**
 * Канонические inside-сцены всех основных сцен меню. В счётчиках участвуют
 * как псевдо-POI родительской карточки (дизайн-правило «Inside the Kaaba
 * считаем как одну POI внутри локации Каабы»), а не собственным туром.
 */
export const MENU_INSIDE_SCENE_IDS: readonly string[] = MENU_MAIN_SCENE_URL_IDS.flatMap(
  (urlId) => getInsideScenesForParent(urlId).map((inside) => inside.id),
);

/**
 * Слайды исторических семей всех основных сцен меню (сейчас — только Кааба),
 * включая слайд «сегодня» (= сама основная сцена). Пусто в Balady-сборке
 * (`IS_BALADY`) — таб Historical Scenes тогда не рендерится вовсе.
 */
export const MENU_HISTORICAL_SLIDES: readonly HistoricalSceneSlide[] =
  MENU_MAIN_SCENE_URL_IDS.flatMap(
    (urlId) => resolveHistoricalFamily(urlId)?.slides ?? [],
  );

const HISTORICAL_POI_FILTER_IDS: readonly string[] = MENU_HISTORICAL_SLIDES.map(
  (slide) => SCENE_POI_ID[slide.sceneId] ?? slide.sceneId,
);

/**
 * Префетч-лист: основные сцены + исторические слайды, отфильтрованные по
 * scene_open-whitelist (стабы эпох без бэкенда не должны слать POST — их
 * карточки показывают «…», пока бэкенд их не поддержит). Inside-сцены НЕ
 * префетчатся: их тур меню больше не показывает (только session-чекбокс).
 */
const PREFETCH_POI_FILTER_IDS: readonly string[] = [
  ...new Set([...MAIN_POI_FILTER_IDS, ...HISTORICAL_POI_FILTER_IDS]),
].filter((id) => isSceneOpenSupported(id));

// --- Session-visited inside scenes (чекбокс «Inside the Kaaba») ---

/**
 * Session-set посещённых inside-сцен (канонические id). Чекбокс в меню +
 * псевдо-POI `visited` в счётчике родительской карточки. Session-scoped
 * СОЗНАТЕЛЬНО (продуктовое решение): сбрасывается перезагрузкой страницы,
 * НЕ сбрасывается навигацией — бэкендового флага «сцена посещена» нет.
 */
export const $visitedInsideScenes = createStore<ReadonlySet<string>>(new Set());

sample({
  clock: $activeSceneId,
  source: $visitedInsideScenes,
  filter: (visited, sceneId): sceneId is string =>
    sceneId != null &&
    isInsideScene(sceneId) &&
    !visited.has(getCanonicalInsideSceneId(sceneId)),
  fn: (visited, sceneId) => {
    const next = new Set(visited);
    next.add(getCanonicalInsideSceneId(sceneId as string));
    return next as ReadonlySet<string>;
  },
  target: $visitedInsideScenes,
});

// --- Per-tab POI aggregates ---

export type MenuPoiProgress = {
  /** Σ visited POI по сценам таба (+ чекнутые inside-псевдо-POI в Hajj). */
  visited: number;
  /** Σ total POI по сценам таба (+ inside-псевдо-POI в Hajj). */
  total: number;
  /** round(visited / total × 100) — большой процент + бар. */
  percent: number;
  /** `false`, пока ни одна сцена таба не получила тур (холодный старт). */
  hasData: boolean;
};

const aggregatePois = (
  ids: readonly string[],
  map: Record<string, SceneProgress>,
): MenuPoiProgress => {
  let visited = 0;
  let total = 0;
  let hasData = false;
  for (const id of ids) {
    const p = map[id];
    if (!p?.hasData) continue;
    hasData = true;
    visited += p.visited;
    total += p.total;
  }
  const percent = total > 0 ? Math.round((visited / total) * 100) : 0;
  return { visited, total, percent, hasData };
};

/**
 * Таб «Hajj Journey»: POI-агрегат по основным сценам + по одному псевдо-POI
 * за каждую inside-сцену (total всегда, visited — когда сцену посещали в
 * этой сессии). Caption и процент считаются из одних и тех же чисел
 * («You have explored X out of Y points of interest»).
 *
 * Псевдо-POI добавляются только когда есть хоть один тур (`hasData`) —
 * иначе холодный старт показал бы бессмысленное «0 out of 1».
 */
export const $hajjJourneyProgress = combine(
  $sceneProgressByPoiFilterId,
  $visitedInsideScenes,
  (map, visitedInside): MenuPoiProgress => {
    const base = aggregatePois(MAIN_POI_FILTER_IDS, map);
    if (!base.hasData) return base;
    const total = base.total + MENU_INSIDE_SCENE_IDS.length;
    const visited =
      base.visited +
      MENU_INSIDE_SCENE_IDS.filter((id) => visitedInside.has(id)).length;
    return {
      visited,
      total,
      percent: total > 0 ? Math.round((visited / total) * 100) : 0,
      hasData: true,
    };
  },
);

/**
 * Таб «Historical Scenes»: POI-агрегат по слайдам исторических семей,
 * включая «Kaaba today» (= POI основной Каабы считаются в ОБОИХ табах —
 * осознанно, по макету). Без inside-псевдо-POI: строка Inside живёт только
 * в Hajj-табе. Стабы эпох без тура просто не вносят вклад.
 */
export const $historicalJourneyProgress = $sceneProgressByPoiFilterId.map(
  (map): MenuPoiProgress => aggregatePois(HISTORICAL_POI_FILTER_IDS, map),
);

/** Fire when the menu opens — kicks off the prefetch for uncached scenes. */
export const menuOpened = createEvent();

const createRequestId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `menu-prefetch-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

type PrefetchResult = {
  poiFilterId: string;
  tour: TourItem[] | null;
  sessionId: string | null;
};

const prefetchSceneTourFx = createEffect<string, PrefetchResult>(
  async (poiFilterId) => {
    const event: SceneOpenEvent = {
      type: 'scene_open',
      sceneId: poiFilterId,
      requestId: createRequestId(),
    };
    const request: EventBusRequest = {
      sessionId: $assistantSessionId.getState() ?? undefined,
      responseFormat: 'markdown',
      // Headless tour fetch: keep `context.sceneId === event.sceneId` (the
      // invariant the navigation-driven scene_open also upholds, since
      // `setSceneId` fires before the request). `currentSight` / `markers`
      // describe the LIVE scene, which may differ from the prefetch
      // target — omit them.
      context: buildEventBusContext({
        currentSight: null,
        markers: [],
        locale: $locale.getState(),
        sceneId: poiFilterId,
      }),
      event,
    };
    const reply = await publishEventBusEventSync(request);
    const replyEvent = reply.event;
    const tour =
      replyEvent.type === 'assistant_message' && Array.isArray(replyEvent.message.tour)
        ? replyEvent.message.tour
        : null;
    return { poiFilterId, tour, sessionId: reply.sessionId ?? null };
  },
);

// Route ONLY the tour into the shared per-scene cache. No chat side-effects.
// `origin: 'prefetch'` tags it as headless so analytics milestone trackers
// (progressTracker) ignore it — the user never entered these scenes.
sample({
  clock: prefetchSceneTourFx.doneData,
  filter: (result) => result.tour !== null,
  fn: (result) => ({
    sceneId: result.poiFilterId,
    items: result.tour as TourItem[],
    origin: 'prefetch' as const,
  }),
  target: tourReceived,
});

// Backend may rotate the sessionId via the reply envelope — keep other
// flows (user_message POST, SSE reconnect) aligned, same as `sceneOpen.ts`.
sample({
  clock: prefetchSceneTourFx.doneData,
  filter: (result) => result.sessionId !== null && result.sessionId.length > 0,
  fn: (result) => result.sessionId as string,
  target: assistantStreamSessionReceived,
});

// Imperative fan-out: one prefetch per uncached menu scene, deduped across
// repeat opens via a module-level in-flight set. A failed prefetch is
// non-fatal (the menu just shows last-known / empty counts).
const inFlight = new Set<string>();

menuOpened.watch(() => {
  const progress = $sceneProgressByPoiFilterId.getState();
  for (const poiFilterId of PREFETCH_POI_FILTER_IDS) {
    if (progress[poiFilterId]?.hasData) continue;
    if (inFlight.has(poiFilterId)) continue;
    inFlight.add(poiFilterId);
    prefetchSceneTourFx(poiFilterId)
      .catch(() => {
        /* non-fatal: leave the scene uncached, retried on next open */
      })
      .finally(() => {
        inFlight.delete(poiFilterId);
      });
  }
});
